import { useEffect, useState } from 'react';
import { Button, Input, Select, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal, EmptyState, Chip } from '../../../shared/components';
import { PlusIcon, EditIcon, TrashIcon } from '../../../shared/icons';
import { getHumanResourcesRepository } from '../../../shared/services/humanResources/repositoryFactory';
import { CalendarDay, CreateCalendarDayInput } from '../../../shared/services/humanResources/types';
import { CalendarImportModal } from './CalendarImportModal';
import { CalendarExcelImportModal } from './CalendarExcelImportModal';
import { StagingReviewTab } from './StagingReviewTab';

const repo = getHumanResourcesRepository();

const WEEKDAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function weekdayOf(dateStr: string): string {
  return WEEKDAYS[new Date(`${dateStr}T00:00:00Z`).getUTCDay()];
}
function quincenaOf(dateStr: string): string {
  const [, m, d] = dateStr.split('-').map(Number);
  return `${d <= 15 ? '1Q' : '2Q'} ${MONTHS[m - 1]}`;
}

const emptyForm: CreateCalendarDayInput = { calendarDate: '', dayOfWeek: '', dayType: 'Ordinario', quincena: '' };

/**
 * Pestaña "Calendario" (Configuración > Parámetros > Calendario) - tabla
 * FINAL de calendario, equivalente literal a la tabla DIAS de PARAMETROS del
 * Excel (Dia, Dia Sem, Tipo Dia, Quincena). Es la única fuente de verdad
 * que consulta el motor de cálculo de Horas Extra.
 *
 * Flujos de carga disponibles:
 *   1. Agregar manualmente — CRUD individual para correcciones puntuales.
 *   2. Cargar por imagen   — OCR → revisión en "Pendiente" → confirmar al final.
 *   3. Cargar festivos por Excel — carga directa al calendario final, con
 *      previsualización antes de confirmar. Usa el archivo de festivos
 *      oficiales generado por el sistema (2026‑2028 disponibles).
 */
export function CalendarTab() {
  const toast = useToast();
  const now = new Date();
  const [subTab, setSubTab] = useState<'final' | 'staging'>('final');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState<number | ''>('');
  const [dayType, setDayType] = useState<'' | 'Ordinario' | 'Festivo'>('');
  const [days, setDays] = useState<CalendarDay[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CalendarDay | null>(null);
  const [form, setForm] = useState<CreateCalendarDayInput>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<CalendarDay | null>(null);
  const [deletingYear, setDeletingYear] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importingExcel, setImportingExcel] = useState(false);

  const load = () => {
    setIsLoading(true);
    repo.listCalendarDays({ year, month: month || undefined, dayType: dayType || undefined })
      .then((d) => { setDays(d); setIsLoading(false); });
  };
  useEffect(() => { load(); }, [year, month, dayType]);

  const openCreate = () => {
    setForm({ ...emptyForm, calendarDate: `${year}-01-01` });
    setCreating(true);
  };
  const openEdit = (d: CalendarDay) => {
    setForm({ ...d });
    setEditing(d);
  };
  const closeModal = () => { setCreating(false); setEditing(null); };

  const onDateChange = (calendarDate: string) => {
    setForm((f) => ({
      ...f,
      calendarDate,
      dayOfWeek: calendarDate ? weekdayOf(calendarDate) : '',
      quincena:  calendarDate ? quincenaOf(calendarDate) : '',
    }));
  };

  const save = async () => {
    if (!form.calendarDate) {
      toast.show('La fecha es obligatoria.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      if (editing) {
        await repo.updateCalendarDay(editing.id, form);
        toast.show('Día actualizado.');
      } else {
        await repo.createCalendarDay(form);
        toast.show('Día agregado.');
      }
      closeModal();
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar el día.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const yearOptions = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1]
    .map((y) => ({ value: String(y), label: String(y) }));
  const monthOptions = [
    { value: '', label: 'Todos los meses' },
    ...MONTHS.map((m, i) => ({ value: String(i + 1), label: m })),
  ];
  const dayTypeOptions = [
    { value: '', label: 'Todos' },
    { value: 'Ordinario', label: 'Ordinario' },
    { value: 'Festivo', label: 'Festivo' },
  ];

  const columns: Column<CalendarDay>[] = [
    { key: 'date',     header: 'Día',      render: (d) => d.calendarDate, sortable: true, sortValue: (d) => d.calendarDate },
    { key: 'weekday',  header: 'Día Sem',  render: (d) => d.dayOfWeek },
    { key: 'type',     header: 'Tipo Día', render: (d) => <Badge tone={d.dayType === 'Festivo' ? 'warning' : 'neutral'}>{d.dayType}</Badge> },
    { key: 'quincena', header: 'Quincena', render: (d) => d.quincena },
    {
      key: 'actions',
      header: '',
      render: (d) => (
        <div className="flex gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />}  label="Editar"    onClick={() => openEdit(d)}      className="h-9 w-9 text-ink-500 hover:bg-ink-100" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar"  onClick={() => setDeleting(d)}   className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Sub-tabs ─────────────────────────────────────────────────────────── */}
      <div className="flex gap-2">
        <Chip label="Calendario final"       active={subTab === 'final'}   onClick={() => setSubTab('final')} />
        <Chip label="Pendiente de revisión"  active={subTab === 'staging'} onClick={() => setSubTab('staging')} />
      </div>

      {subTab === 'staging' ? (
        <>
          <Select
            label="Año"
            options={yearOptions}
            value={String(year)}
            onChange={(e) => setYear(Number(e.target.value))}
            className="max-w-[200px]"
          />
          <StagingReviewTab year={year} />
        </>
      ) : (
        <>
          {/* Filtros ───────────────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Select label="Año"      options={yearOptions}    value={String(year)}  onChange={(e) => setYear(Number(e.target.value))} />
            <Select label="Mes"      options={monthOptions}   value={String(month)} onChange={(e) => setMonth(e.target.value ? Number(e.target.value) : '')} />
            <Select label="Tipo Día" options={dayTypeOptions} value={dayType}       onChange={(e) => setDayType(e.target.value as '' | 'Ordinario' | 'Festivo')} />
          </div>

          {/* Acciones ──────────────────────────────────────────────────────── */}
          <div className="flex flex-wrap gap-3">
            <Button size="md" leftIcon={<PlusIcon width={18} height={18} />} onClick={openCreate}>
              Agregar día manualmente
            </Button>
            <Button size="md" variant="outline" onClick={() => setImporting(true)}>
              📷 Cargar por imagen
            </Button>
            <Button size="md" variant="outline" onClick={() => setImportingExcel(true)}>
              📥 Cargar festivos por Excel
            </Button>
            <Button
              size="md"
              variant="outline"
              className="!text-critical !border-critical/40 ml-auto"
              onClick={() => setDeletingYear(true)}
            >
              Eliminar todo el cargue de {year}
            </Button>
          </div>

          {/* Tabla / EmptyState ─────────────────────────────────────────────── */}
          {days.length === 0 && !isLoading ? (
            <EmptyState
              icon={
                <div className="flex flex-col items-center gap-1">
                  <span className="text-5xl">📅</span>
                </div>
              }
              title={`Sin días cargados para ${year}`}
              description={
                `No hay días registrados en el calendario final de ${year}. ` +
                `Usa "📥 Cargar festivos por Excel" para importar todos los festivos de una vez, ` +
                `"📷 Cargar por imagen" para el calendario completo desde una foto, ` +
                `o agrega días individualmente con el botón de arriba.`
              }
              action={
                <div className="flex flex-wrap gap-3 justify-center mt-2">
                  <Button variant="primary" onClick={() => setImportingExcel(true)}>
                    📥 Cargar festivos por Excel
                  </Button>
                  <Button variant="outline" onClick={() => setImporting(true)}>
                    📷 Cargar por imagen
                  </Button>
                </div>
              }
            />
          ) : (
            <DataTable
              columns={columns}
              rows={days}
              rowKey={(d) => d.id}
              isLoading={isLoading}
              emptyTitle="Sin días"
            />
          )}
        </>
      )}

      {/* Modal — carga por imagen ─────────────────────────────────────────── */}
      <CalendarImportModal
        open={importing}
        onClose={() => setImporting(false)}
        defaultYear={year}
        onImported={() => {
          setSubTab('staging');
          load();
        }}
      />

      {/* Modal — carga festivos por Excel ────────────────────────────────── */}
      <CalendarExcelImportModal
        open={importingExcel}
        onClose={() => setImportingExcel(false)}
        onImported={() => {
          toast.show('Festivos importados al calendario final.');
          load();
        }}
      />

      {/* Modal — crear / editar día ──────────────────────────────────────── */}
      <Modal open={creating || !!editing} onClose={closeModal} title={editing ? 'Editar día' : 'Agregar día'}>
        <div className="space-y-3">
          <Input
            label="Fecha"
            type="date"
            value={form.calendarDate}
            onChange={(e) => onDateChange(e.target.value)}
          />
          <Input
            label="Día Sem"
            value={form.dayOfWeek}
            onChange={(e) => setForm((f) => ({ ...f, dayOfWeek: e.target.value }))}
            hint="Se autocompleta según la fecha, pero puedes corregirlo."
          />
          <Select
            label="Tipo Día"
            options={[{ value: 'Ordinario', label: 'Ordinario' }, { value: 'Festivo', label: 'Festivo' }]}
            value={form.dayType}
            onChange={(e) => setForm((f) => ({ ...f, dayType: e.target.value as 'Ordinario' | 'Festivo' }))}
          />
          <Input
            label="Quincena"
            value={form.quincena}
            onChange={(e) => setForm((f) => ({ ...f, quincena: e.target.value }))}
            hint='Ej. "1Q Enero"'
          />
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={closeModal}>Cancelar</Button>
            <Button fullWidth onClick={save} isLoading={isSaving}>Guardar</Button>
          </div>
        </div>
      </Modal>

      {/* Confirm — eliminar día ──────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!deleting}
        title="Eliminar día"
        description={`¿Eliminar el ${deleting?.calendarDate}? Los turnos ya registrados en esa fecha dejarán de clasificarse hasta que se vuelva a cargar.`}
        tone="danger"
        confirmLabel="Eliminar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await repo.deleteCalendarDay(deleting.id);
          setDeleting(null);
          load();
        }}
      />

      {/* Confirm — eliminar año ──────────────────────────────────────────── */}
      <ConfirmDialog
        open={deletingYear}
        title={`Eliminar TODO el calendario de ${year}`}
        description={`Esta acción borra los ${days.length} días cargados de ${year} en el calendario final. Los turnos registrados en ese año dejarán de clasificarse hasta que se vuelva a cargar. Esta es la válvula de escape para cuando los datos se corrompen — úsala con cuidado.`}
        tone="danger"
        confirmLabel={`Eliminar todo ${year}`}
        onCancel={() => setDeletingYear(false)}
        onConfirm={async () => {
          const count = await repo.deleteCalendarYear(year);
          toast.show(`Se eliminaron ${count} días de ${year}.`);
          setDeletingYear(false);
          load();
        }}
      />
    </div>
  );
}
